import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Chip,
  IconButton, Tooltip, Alert, CircularProgress, MenuItem
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const estadoColor = { ABIERTO: 'success', CERRADO: 'error', BLOQUEADO: 'warning' };

export default function Periodos() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [periodos, setPeriodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openAnio, setOpenAnio] = useState(false);
  const [form, setForm] = useState({ periodo: '', nombre: '' });
  const [anioGen, setAnioGen] = useState(new Date().getFullYear());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    axios.get('/api/contabilidad/empresas')
      .then(r => {
        const todas = Array.isArray(r.data) ? r.data : [];
        setEmpresas(todas);
        const activas = todas.filter(e => e.activo);
        if (activas.length === 1) setEmpresaId(String(activas[0].id));
      })
      .catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const r = await axios.get('/api/contabilidad/periodos', { params: { empresa_id: empresaId } });
      setPeriodos(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError('Error al cargar períodos');
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const cerrar = async (id) => {
    if (!window.confirm('¿Cerrar este período? No se podrán registrar más asientos.')) return;
    try {
      await axios.put(`/api/contabilidad/periodos/${id}/cerrar`);
      setSuccess('Período cerrado correctamente');
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const reabrir = async (id) => {
    if (!window.confirm('¿Reabrir este período?')) return;
    try {
      await axios.put(`/api/contabilidad/periodos/${id}/reabrir`);
      setSuccess('Período reabierto');
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const crearPeriodo = async () => {
    if (!form.periodo || form.periodo.length !== 6) {
      setError('Ingrese el período en formato YYYYMM (ej: 202601)');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.post('/api/contabilidad/periodos', { empresa_id: empresaId, ...form });
      setSuccess('Período creado');
      setOpen(false);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const generarAnio = async () => {
    setSaving(true);
    setError('');
    try {
      const r = await axios.post('/api/contabilidad/periodos/generar-anio', {
        empresa_id: empresaId, anio: anioGen
      });
      setSuccess(`${r.data.creados} períodos generados para el año ${anioGen}`);
      setOpenAnio(false);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const formatPeriodo = (p) => {
    if (!p || p.length < 6) return p;
    const mes = parseInt(p.slice(4, 6)) - 1;
    return `${MESES[mes]} ${p.slice(0, 4)}`;
  };

  // Agrupar por año
  const porAnio = {};
  periodos.forEach(p => {
    if (!porAnio[p.anio]) porAnio[p.anio] = [];
    porAnio[p.anio].push(p);
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <CalendarMonthIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight="bold">Períodos Contables</Typography>
        </Box>
        <TextField select label="Empresa" size="small" value={empresaId}
          onChange={e => setEmpresaId(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 260 }, ml: { sm: 'auto' } }}>
          {empresas.map(e => (
            <MenuItem key={e.id} value={String(e.id)}>
              {e.razon_social}{!e.activo && <span style={{ color: '#999', marginLeft: 6 }}>(Inactiva)</span>}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && !open && !openAnio && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {!empresaId ? (
        <Alert severity="info">Seleccione una empresa para gestionar sus períodos contables.</Alert>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
            <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => { setOpenAnio(true); setError(''); }}>
              Generar año completo
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ periodo: '', nombre: '' }); setError(''); setOpen(true); }}>
              Nuevo período
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
          ) : periodos.length === 0 ? (
            <Alert severity="info">
              No hay períodos registrados. Use "Generar año completo" para crear los 12 meses de un año de una vez.
            </Alert>
          ) : (
            Object.keys(porAnio).sort((a, b) => b - a).map(anio => (
              <Box key={anio} sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
                  Año {anio}
                </Typography>
                <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        {['Período', 'Nombre', 'Estado', 'Apertura', 'Cierre', 'Cerrado por', 'Acciones'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {porAnio[anio].sort((a, b) => a.mes - b.mes).map(p => (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{p.periodo}</TableCell>
                          <TableCell>{p.nombre || formatPeriodo(p.periodo)}</TableCell>
                          <TableCell>
                            <Chip label={p.estado} size="small"
                              color={estadoColor[p.estado] || 'default'} />
                          </TableCell>
                          <TableCell>{p.fecha_apertura?.slice(0, 10) || '—'}</TableCell>
                          <TableCell>{p.fecha_cierre?.slice(0, 10) || '—'}</TableCell>
                          <TableCell>{p.usuario_cierre || '—'}</TableCell>
                          <TableCell>
                            {p.estado === 'ABIERTO' ? (
                              <Tooltip title="Cerrar período">
                                <IconButton size="small" color="error" onClick={() => cerrar(p.id)}>
                                  <LockIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Reabrir período">
                                <IconButton size="small" color="success" onClick={() => reabrir(p.id)}>
                                  <LockOpenIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))
          )}
        </>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nuevo Período Contable</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField label="Período (YYYYMM)" value={form.periodo} fullWidth size="small"
                onChange={e => setForm(f => ({ ...f, periodo: e.target.value }))}
                helperText="Ejemplo: 202601 para Enero 2026"
                inputProps={{ maxLength: 6 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Nombre (opcional)" value={form.nombre} fullWidth size="small"
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                placeholder={`Ej: ${formatPeriodo(form.periodo) || 'Enero 2026'}`} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={crearPeriodo} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Crear período'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAnio} onClose={() => setOpenAnio(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generar Año Completo</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Alert severity="info" sx={{ mb: 2 }}>
            Se crearán los 12 períodos del año seleccionado. Los que ya existen no se duplicarán.
          </Alert>
          <TextField label="Año" type="number" value={anioGen} fullWidth size="small"
            onChange={e => setAnioGen(e.target.value)}
            inputProps={{ min: 2000, max: 2100 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAnio(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={generarAnio} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : `Generar ${anioGen}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
